import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';

export function getValidators(f): ValidatorFn[] {
    const validators = [];
    if (f.validators) {
        if ((f.validators.required && !f.disabled)) {
            validators.push(required(f.label));
        }
        if (f.validators.isNumber) {
            validators.push(isNumber(f.label));
        }
        if (f.validators.variableCheck) {
          validators.push(variableCheck(f.label));
        }
        if (f.validators.variableCheckSession) {
          validators.push(variableCheckSession(f.label));
        }
        if (f.validators.maxLength) {
            validators.push(maxLength(f.validators.maxLength.length, f.validators.maxLength.message));
        } else {
            validators.push(maxLength(f.validators.maxLength));
        }
        if (f.validators.hasDuplicates) {
            validators.push(hasDuplicates());
        }
        if (f.validators.optionSelected) {
            validators.push(optionSelected());
        }
        if (f.validators.hasNegativeNumber) {
            validators.push(hasNegativeNumbers());
        }
        if (f.validators.noSpaces) {
            validators.push(noSpaces());
        }
        if (f.validators.minimumCount) {
            validators.push(minimumCount(f.validators.minimumCount, f.label));
        }
        if (f.validators.spaceOnName) {
            validators.push(spaceOnName());
        }
        if (f.validators.allForOne) {
            validators.push(allForOne(f.label, f.validators.allForOne));
        }
        if (f.validators.latitudeCheck) {
            validators.push(latitudeCheck(f.validators.latitudeCheck.message));
        }
        if (f.validators.longitudeCheck) {
            validators.push(longitudeCheck(f.validators.longitudeCheck.message));
        }
        if (f.validators.noSpacesNoText) {
            validators.push(noSpacesNoText(f.label));
        }
        // if (f.validators.isDuplicated) {
        //     validators.push(isDuplicated(f.name))
        // }
    }
    return validators;
}

export const validHeader = (): ValidatorFn => {
  return (formGroup: FormGroup) => {
    const dropDown = formGroup.get('menu.menuParams.displayType');
    const menuHeader = formGroup.get('menu.menuHeader');
    const imageRef = formGroup.get('menu.imageRef');

    if ((dropDown.value === 'button' || dropDown.value === 'auto' || imageRef.value !== '')) {
      if (Validators.required(menuHeader)) {
        menuHeader.setErrors({invalidLocator: 'Menu Header is required'});
        return null;
      }
    }
    menuHeader.setErrors(undefined);
    return null;
  };
};

export function dropdownObjectValidation(label: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (typeof control.value === 'string') {
            return null; /* valid option selected */
        }
        return  { 'dropdownError': label +' is not recognized' }
    };
}

export function required(label: string, message?: string): ValidatorFn {
  if (!message) {
    if (label !== 'null' && label !== '#' && label !== '$') {
      message = label + ' is required';
    } else {
      message = 'Field is required';
    }
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = Validators.required(control);
    return forbidden ? { required: message } : null;
  };
}

export function msisdnValidator(label: string): ValidatorFn {
    const errorMessage = `Invalid ${label}. ${label} must either be a valid ChatFlow variable or follow the international standard.`;

    // create a JavaScript regex pattern to validate international phone numbers, the string can optionally start with
    // a + symbol, the string can optionally contain one opening and one closing parenthesis within the first 4
    // characters, the can optionally contain spaces and dashes
    const msisdnRegex = /^\+?[ \d-]*\d{0,4}(\(\d+\))?[ \d-]*\d+$/;

    return (control: AbstractControl): { [key: string]: any } | null => {
        // empty value, not validated here
        if (!control.value) {
            return null;
        }

        //  flow variable syntax
        if (control.value.startsWith('[[') && control.value.endsWith(']]')) {
            return null;
        } else  {
            const forbidden = !msisdnRegex.test(control.value);
            return forbidden ? { invalidMSISDN: errorMessage } : null;
        }
    };
}

export function flowNameValidator(): ValidatorFn {
    const errorMessage = `Invalid Flow name`;

    const flowNameRegex = /^\w[\w'"`!&*()-+=;:<>,.?]*$|^(\w[\w'"`!&*()-+=;:<>,.?]*[\/])+\w[\w'"`!&*()-+=;:<>,.?]*$/;

    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return {invalidFlowName: 'Flow name is required'};
        }

        const forbidden = !flowNameRegex.test(control.value);
        return forbidden ? { invalidFlowName: errorMessage } : null;
    };
}


export function dateValidator(label: string = "date"): ValidatorFn {
    const datePattern = /^(19|20)\d{2}-(0[1-9]|1[1,2,0])-(0[1-9]|[12][0-9]|3[01])$/;
    const result = { invalidDateFormat: `Invalid ${label} Format` };
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return result;
        }
        const forbidden = !datePattern.test(control.value);
        return forbidden ? result : null;
    };
}

export function urlValidator(label: string = "Url"): ValidatorFn {

    const emailPatter = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+/;

    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return { invalidUrl: `${label} is required` };
        }
        const forbidden = !emailPatter.test(control.value);
        return forbidden ? { invalidUrl: `Invalid ${label}` } : null;
    };
}

export function phoneNumberValidator(label: string): ValidatorFn {

    // NB: this will only match SA cellphone numbers
    const phoneNumberPattern = /^(\+27|0)[6-8][0-9]{8}$/;

    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return null;
        }
        const forbidden = !phoneNumberPattern.test(control.value);
        return forbidden ? { invalidPhoneNumber: `Invalid ${label}` } : null;
    };
}

export function isNumber(message?): ValidatorFn {
    message = 'Value must be a number';
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = isNaN(control.value);
        return forbidden ? { 'isNumber': message } : null;
    };
}

export function isValidName(message?): ValidatorFn {
  if (!message) {
    message = 'Value Can`t contain \':\' ';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = control.value.indexOf(':') !== -1;
    return forbidden ? { inValidName: message } : null;
  };
}

export function variableCheck(message?): ValidatorFn {
  if (!message) {
    message = 'Value Can`t start with \'[[\' or end with \']]\' ';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const regex = /\[\[|]]/;
    const forbidden = regex.test(control.value.trim());
    return forbidden ? { inValidName: message } : null;
  };
}

export function variableCheckSession(message?): ValidatorFn {
  if (!message) {
    message = 'Value Can`t start with \'session:\' ';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = control.value.indexOf('session:') !== -1 && control.value.indexOf('session:') === 0;
    return forbidden ? { inValidName: message } : null;
  };
}

export function noSpaces(message?): ValidatorFn {
  if (!message) {
    message = 'Value Can`t contain spaces';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = control.value.indexOf(' ') !== -1;
    return forbidden ? { inValidName: message } : null;
  };
}

export function noWhitespaceValidator(label: string = 'Field'): ValidatorFn {
    const message = `${label} cannot contain only spaces.`;
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }
        const isValid = control.value.trim().length === 0;
        return isValid ? { 'onlyWhitespaceInvalid': message } : null;
    };
}

export function noSpacesNoText(label: string): ValidatorFn {
    const message = `${label} is required`;

    return (control: AbstractControl): { [key: string]: any } | null => {
        const regex = /^\s+$/;
        const forbidden = regex.test(control.value);
        return forbidden ? { onlySpacesInvalid: message } : null;
    };
}

export function isDuplicatedName(values: Array<string>, declaredValue, message?): ValidatorFn {
  if (!message) {
    message = 'duplicated Value found';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.dirty) {
      const forbidden = declaredValue !== control.value && values.includes(control.value);
      return forbidden ? { duplicatedName: message } : null;
    }
  };
}

export function additionalDataMaxLength(nameControl: AbstractControl, entityId: string, message?: string): ValidatorFn {
    if (!message) {
        message = 'Max length exceeded';
    }

    return (control: AbstractControl): { [key: string]: any } | null  => {
        if (!control.get('additionalData') || !control.get('flowStep')) { return; }
        const additionalParameterValue: string = control.get('additionalData').value;
        const flowStepValue: string = control.get('flowStep').value;

        if (additionalParameterValue.startsWith('[[') && additionalParameterValue.endsWith(']]')) { return; }

        const maxPostbackSize = 149;
        const buffer = 9;
        const jsonChars = 65;
        const flowIdLength = 32;

        const stepIdLength = flowStepValue.substring(flowStepValue.indexOf(':') + 1, flowStepValue.length).length + flowIdLength;
        const nameLength = nameControl.value.length;
        const additionalParameterLength = additionalParameterValue.length;
        const entityIdLength = entityId.length;
        const entityTypeLength = 5; // entityType = client

        const allowedLength = ( maxPostbackSize - buffer ) - ( jsonChars + stepIdLength + entityIdLength + entityTypeLength + nameLength);

        const forbidden = additionalParameterLength >= allowedLength;
        return forbidden ? { additionalDataMaxLength: message } : null;
    };
}

export function maxLength(length: number, message?: string,containsVariable?:boolean): ValidatorFn {
  containsVariable = false;
  if (!message) {
    message = 'Max length exceeded';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      if (!value.length) { return; }
      if (typeof value === 'string') {
          if (value.startsWith('[[') && value.endsWith(']]')) {
              containsVariable = true;
              return;
          }
      }

      if (containsVariable ===true) {
          containsVariable = false;
          return;
      }

      const forbidden = value.length > length;
      return forbidden ? { maxLength: message } : null;
  };
}

export function longitudeCheck(message?: string): ValidatorFn {
  const LONGITUDE_REGEXP =
    /^(\+|-)?((\d((\.)|\.\d{1,10})?)|(0*?\d\d((\.)|\.\d{1,10})?)|(0*?1[0-7]\d((\.)|\.\d{1,10})?)|(0*?180((\.)|\.0{1,10})?))$/;
  const VARIABLE_REGEXP = /^\[\[[\w\s\W]+\]\]$/;
  if (!message) {
    message = 'Longitude is not valid';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    if (value.length > 0) {
      if (LONGITUDE_REGEXP.test(value) || VARIABLE_REGEXP.test(value)) {
        return null;
      } else {
        return {
          longitudeCheck: message,
        };
      }
    } else {
      return null;
    }
  };
}

export function latitudeCheck(message?: string): ValidatorFn {
  const LATITUDE_REGEXP =
    /^(\+|-)?((\d((\.)|\.\d{1,10})?)|(0*?[0-8]\d((\.)|\.\d{1,10})?)|(0*?90((\.)|\.0{1,10})?))$/;
  const VARIABLE_REGEXP = /^\[\[[\w\s\W]+\]\]$/;
  if (!message) {
    message = 'Latitude is not valid';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    if (value.length > 0) {
      if (LATITUDE_REGEXP.test(value) || VARIABLE_REGEXP.test(value)) {
        return null;
      } else {
        return {
          latitudeCheck: message,
        };
      }
    } else {
      return null;
    }
  };
}

export function optionSelected(message?: string): ValidatorFn {
  if (!message) {
    message = 'Please select an Option';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    const forbidden = value
      ? value.indexOf('-Not Selected-') !== -1 || value === '' || value === '-'
      : false;
    return forbidden ? { optionSelected: message } : null;
  };
}

export function hasDuplicates(message?: string): ValidatorFn {
  if (!message) {
    message = 'Value already exist';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    if (control.dirty) {
      const forbidden = checkForDuplicates(value);
      return forbidden ? { hasDuplicates: message } : null;
    }
  };
}

export function hasNegativeNumbers(message?: string): ValidatorFn {
  if (!message) {
    message = 'No negative numbers allowed';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value: number = control.value;
    if (control.dirty) {
      const forbidden = value < 0;
      return forbidden ? { hasNegativeNumber: message } : null;
    }
  };
}

export function minimumCount(minimumCount: number, label: string): ValidatorFn {
  const message = `${label} can\`t be less then ${minimumCount}`;

  return (control: AbstractControl): { [key: string]: any } | null => {
    const value: number = control.value;
    const forbidden = value < minimumCount;
    return forbidden ? { minimumCount: message } : null;
  };
}

export function maximumCount(maximumCount: number, label: string): ValidatorFn {
    const message = `${label} can\`t be more then ${maximumCount}`;

    return (control: AbstractControl): { [key: string]: any } | null => {
        const value: number = control.value;
        const forbidden = value > maximumCount;
        return forbidden ? { maximumCount: message } : null;
    };
}

export function isValidGroupingSeparator(label: string): ValidatorFn {
    const message = `${label} can\`t be more then 1 character`;
    return (control: AbstractControl): { [key: string]: any } | null => {
        const value: string = control.value;
        if (value === 'none') {
          return null;
        }
        const forbidden = value.length > 1;
        return forbidden ? { maximumLength: message } : null;
    };
}

export function isValidVariable(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {

        const invalid = { required: 'Invalid variable name' }

        if (control.value === '') {
            return invalid
        }

        if (!control.value.startsWith('[[') || !control.value.endsWith(']]')) {
            return invalid
        }

        if (control.value.length <= 4) {
            return invalid
        }

        return null;
    };
}

export function spaceOnName(message?: string): ValidatorFn {
  if (!message) {
    message = 'Name can`t contain space';
  }
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = /\s/.test(control.value);
    return forbidden ? { spaceOnName: message } : null;
  };
}

export function specialCharsNotAllowed(message?: string): ValidatorFn {
    if (!message) {
        message = 'No special characters allowed';
    }
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = !/(^$)|^([-\sA-Za-z0-9]){0,}$/.test(control.value);
        return forbidden ? { specialChars: message }: null;
    };
}

export function checkForDuplicates(array) {
  const valuesSoFar = Object.create(null);
  for (let i = 0; i < array.length; ++i) {
    const value = array[i];
    if (value in valuesSoFar) {
      return true;
    }
    valuesSoFar[value] = true;
  }
  return false;
}

export function allForOne(label: string, included?: string[]): ValidatorFn {
  const validate = (controls: string[], parent: FormGroup) => {
    for (const controlName of controls) {
      if (parent && parent.get(controlName)) {
        const linkedControl = parent.get(controlName);
        if (containsLenghtOfOneOrMore(controls, parent) && linkedControl.value.length === 0) {
          const controlNameUppercase = controlName.charAt(0).toUpperCase() + controlName.slice(1);
          linkedControl.setErrors({ allForOne: `${controlNameUppercase} is required` });
          linkedControl.markAsTouched();
        } else if (linkedControl.getError('allForOne')) {
          if (!containsLenghtOfOneOrMore(controls, parent) || linkedControl.value.length !== 0) {
            const error = linkedControl.errors;
            delete error['allForOne'];
            linkedControl.setErrors(error);
            linkedControl.updateValueAndValidity();
          }
        }
      }
    }
  };

  const containsLenghtOfOneOrMore = (controls: string[], parent: FormGroup) => {
    return (
      controls.filter((ctrlName) => {
        const control = parent.get(ctrlName);
        if (control.value.length > 0) {
          return true;
        }
      }).length > 0
    );
  };

  return (control: AbstractControl): { [key: string]: any } | null => {
    const parent = control.parent as FormGroup;
    validate(included, parent);
    if (parent) {
      return control.value.length <= 0 && containsLenghtOfOneOrMore(included, parent)
        ? { allForOne: `${label} is required ` }
        : null;
    } else {
      return control.value.length <= 0 ? { allForOne: `${label} is required` } : null;
    }
  };
}

export function regExpValidation(regexp: string, label: string): ValidatorFn {
    const message = `${label} is not valid`;
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = new RegExp(regexp).test(control.value);
        return (forbidden
            ? null
            : control.value
                ? { regExp: message }
                : null);
    };
}

export function variableOrValueLength(message?): ValidatorFn {
    if (!message) {
        message = 'Value should be variable or 32char length';
    }
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return null;
        }
        const forbidden = control.value !== '' && !(control.value.startsWith('[[') && control.value.endsWith(']]'));
        if (forbidden) {
            return control.value.trim().length !== 32 ? { variableOrValueLength: message } : null;
        }
        return null;
    };
}

export function pattern(patternToMatch: RegExp, message?: string): ValidatorFn {
    if (!message) {
        message = 'Invalid input';
    }

    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return null;
        }
        const forbidden = !patternToMatch.test(control.value);
        return forbidden ? { invalidPattern: message } : null;
    };
}
